/**
 * User compatibility utilities for creating v3-compatible user objects
 * Used to bridge Amplify v6 with legacy code expecting v3 structure
 */

import {extractTokenPayload} from '@/router/token-utils'

/**
 * Creates a v3-compatible user object from Amplify v6 userAttributes and session
 *
 * This utility ensures consistent user object structure across the application,
 * whether authentication is via regular Cognito User Pool or OIDC/Panex.
 *
 * @param userAttributes - User attributes from authAdapter.fetchUserAttributes()
 * @param session - Session from authAdapter.getCurrentSession()
 * @returns v3-compatible user object with getUsername() and getSignInUserSession() methods
 */
export function createCompatibleUser(userAttributes: any, session: any) {
  // Extract token payload based on auth method (handles both Cognito and Panex)
  const tokenPayload = extractTokenPayload(session)

  // Create a v3-compatible user object for legacy code
  const compatibleUser = {
    attributes: userAttributes,
    username: userAttributes.preferred_username || userAttributes.email,
    getUsername: () => userAttributes.preferred_username || userAttributes.email,
    signInUserSession: {
      accessToken: {
        payload: tokenPayload,
      },
      idToken: {
        jwtToken: session.tokens?.idToken?.toString() || session.tokens?.idToken || '',
      },
    },
    getSignInUserSession: () => ({
      getAccessToken: () => ({
        decodePayload: () => {
          // Get groups from token payload
          const tokenGroups = tokenPayload['cognito:groups']

          // Fallback to user attributes
          const attrGroups = userAttributes['cognito:groups']

          // Use whichever source has groups
          const groups = tokenGroups || attrGroups
          const groupsArray =
            typeof groups === 'string' && groups
              ? groups.split(',').map((g: string) => g.trim())
              : Array.isArray(groups)
                ? groups
                : []

          return {
            ...tokenPayload,
            ...userAttributes,
            'cognito:groups': groupsArray,
          }
        },
      }),
    }),
  }

  return compatibleUser
}
